import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Avatar,
  Button,
  Box,
  Chip,
  Grid,
  CircularProgress,
} from "@mui/material";
import moment from "moment";
import { getDepartmentMedal, getManagementMedal } from "./VoteCard";
import { connect } from "react-redux";
import {
  generateBestEmployeeCertificate,
  getVoteDetails,
  getVotingBehaviors,
  getVotingRatings,
} from "../../../store/voting/actions";
import { useEffect, useState } from "react";
import PreviewPdfUrl from "../../common/components/PreviewPdfUrl";

const VoteDetailsDialog = ({
  open,
  onClose,
  voteData,
  voteBehaviors,
  voteRatings,
  getVotingBehaviors,
  getVotingRatings,
  getVoteDetails,
  generateBestEmployeeCertificate,
}) => {
  const [loadingData, setLoadingData] = useState(true);
  const [ratedBehaviors, setRatedBehaviors] = useState([]);
  const [pivotVoteData, setPivotVoteData] = useState([]);
  const [onViewDocument, setOnViewDocument] = useState(false);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [certificateDocRefNo, setCertificateDocRefNo] = useState(
    voteData?.certificateDocRefNo
  );

  useEffect(() => {
    const grouped = {};
    const behaviors = [];

    ratedBehaviors.forEach(({ voterId, voterName, behaviorName, rateName }) => {
      if (!grouped[voterId]) {
        grouped[voterId] = { voterName };
      }
      grouped[voterId][behaviorName] = rateName;

      if (!behaviors.includes(behaviorName)) {
        behaviors.push(behaviorName);
      }
    });

    setPivotVoteData({
      headers: ["#", ...behaviors],
      rows: Object.entries(grouped).map(([voterId, details], index) => ({
        "#": `Voter${index + 1}`,
        ...behaviors.reduce((acc, behavior) => {
          acc[behavior] = details[behavior] || "";
          return acc;
        }, {}),
      })),
    });
  }, [ratedBehaviors]);

  useEffect(() => {
    if (voteRatings?.length === 0) {
      getVotingRatings(setLoadingData);
    } else setLoadingData(false);

    if (voteBehaviors?.length === 0) {
      getVotingBehaviors(setLoadingData);
    } else setLoadingData(false);

    if (!!voteData.employeeVoteId) {
      getVoteDetails(voteData.employeeVoteId, (status, data) => {
        setLoadingData(status);
        setRatedBehaviors(data);
      });
    } else setLoadingData(false);

    return () => {
      setLoadingData(true);
    };
  }, []);

  if (!voteData) return null;

  const {
    names,
    positionName,
    fiscalYearName,
    departmentNo,
    managementNo,
    voterCount,
    behaviorRate,
    typeId,
    votedOn,
    comment,
    passedOnDepartmentLevel,
    passedOnManagementLevel,
  } = voteData;

  const managementMedal =
    typeId === "M" && !!passedOnManagementLevel
      ? getManagementMedal(managementNo)
      : null;
  const departmentMedal =
    typeId === "D" && !!passedOnDepartmentLevel
      ? getDepartmentMedal(departmentNo)
      : null;

  const isDepartmentPublished = passedOnDepartmentLevel !== null;
  const isManagementPublished =
    !!isDepartmentPublished && passedOnManagementLevel !== null;
  const isPublished = isDepartmentPublished || isManagementPublished;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        style: {
          borderRadius: "16px",
          padding: "20px",
        },
      }}
    >
      <DialogTitle textAlign="center">
        <Avatar
          sx={{
            bgcolor: "#1976d2",
            width: 70,
            height: 70,
            margin: "0 auto",
            fontSize: "2rem",
          }}
        >
          {names.charAt(0)}
        </Avatar>
        <Typography variant="h5" mt={2}>
          {names}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          {positionName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Winner Badge */}
        <Box mb={2} textAlign="center">
          {managementMedal && (
            <>
              <Typography variant="h3" mb={1}>
                {managementMedal.medal}
              </Typography>
              <Chip
                label={managementMedal.label}
                style={{
                  backgroundColor: managementMedal.color,
                  color:
                    managementMedal.color === "#FFD700" ? "#333333" : "white",
                  fontWeight: "bold",
                }}
              />
            </>
          )}
          {departmentMedal && (
            <>
              <Typography variant="h3" mb={1}>
                {departmentMedal.trophy}
              </Typography>
              <Chip
                label={departmentMedal.label}
                style={{
                  backgroundColor: departmentMedal.color,
                  color: "white",
                  fontWeight: "bold",
                }}
              />
            </>
          )}
        </Box>

        <Typography variant="body1" gutterBottom>
          <strong>Fiscal Year:</strong> {fiscalYearName}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>
            {managementMedal ? "Management No" : "Department No"}:
          </strong>{" "}
          {isPublished ? (managementMedal ? managementNo : departmentNo) : "-"}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Total Score:</strong> {isPublished ? voterCount : "-"}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Total Rate:</strong> {isPublished ? behaviorRate : "-"}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Voted On:</strong> {moment(votedOn).format("LL")}
        </Typography>

        {/* Behavior Ratings */}
        <Box mt={3}>
          <Typography variant="h6" gutterBottom>
            Ratings:
          </Typography>
          <Grid container spacing={1}>
            <VoteTable
              headers={pivotVoteData.headers}
              rows={pivotVoteData.rows}
            />
          </Grid>
        </Box>

        {/* Comment */}
        {comment && (
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>
              Comment:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {comment}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: "column", gap: 1 }}>
        {managementMedal && (
          <>
            <Button
              variant="outlined"
              color="success"
              fullWidth
              disabled={loadingDocument}
              onClick={() => {
                if (!certificateDocRefNo) {
                  generateBestEmployeeCertificate(voteData, (status, res) => {
                    setLoadingDocument(status);
                    if (!status && !!res) {
                      setCertificateDocRefNo(res);
                      setOnViewDocument(true);
                    }
                  });
                } else {
                  setOnViewDocument(true);
                }
              }}
            >
              View Certificate
            </Button>
            {loadingDocument && (
              <CircularProgress
                sx={{
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                  color: "rgb(7, 142, 206)",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
                size={40}
                thickness={4}
              />
            )}
          </>
        )}
        <Button onClick={onClose} variant="contained" fullWidth>
          Close
        </Button>
      </DialogActions>

      {onViewDocument && !!certificateDocRefNo && (
        <PreviewPdfUrl
          showDocument={onViewDocument}
          setShowDocument={() => setOnViewDocument(false)}
          document={{
            name: `Best Employee Certificate`,
            url:
              process.env.REACT_APP_FILES_URL +
              "/preview/" +
              certificateDocRefNo,
          }}
        />
      )}
    </Dialog>
  );
};

const mapStateToProps = ({ env, loading, voteBehaviors, voteRatings }) => {
  return { env, loading, voteBehaviors, voteRatings };
};

export default connect(mapStateToProps, {
  getVotingBehaviors,
  getVotingRatings,
  getVoteDetails,
  generateBestEmployeeCertificate,
})(VoteDetailsDialog);

const VoteTable = ({ headers, rows }) => (
  <table className="table table-bordered table-sm">
    <thead>
      <tr>
        {headers.map((h) => (
          <th key={h}>{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((row, i) => (
        <tr key={i}>
          {headers.map((h) => (
            <td key={h}>{row[h] || ""}</td>
          ))}
        </tr>
      ))}
    </tbody>
      
  </table>
);
